// createTokenRenewer.js
import { customFetch as customFetchLog } from '../resource/debug.js';
import { TOKEN_EXPIRED_THRESHOLD } from '../RemoteConfigKeys.js';

const log = customFetchLog.extend('log');
const errorLog = customFetchLog.extend('error');

/**
 * Create token renewer function for customFetch.
 *
 * The returned function is intended to be passed to `setTokenRenewer` from
 * `customFetch.js` and is only ever invoked from the main app (not the
 * Service Worker). Keeping its dynamic imports out of `customFetch.js`
 * stops webpack from inlining the chunk filename map into the SW bundle.
 *
 * @param {object} params
 * @param {function} params.getState - redux store getState
 * @param {function} params.dispatch - redux store dispatch
 * @returns {function} A tokenRenewer function: `({ url, options }) => Promise<{ url, options }>`.
 * @example
 * import { setTokenRenewer } from '../resource/customFetch.js';
 * import { createTokenRenewer } from '../resource/createTokenRenewer.js';
 *
 * setTokenRenewer({
 *   tokenRenewer: createTokenRenewer({ getState, dispatch }),
 * });
 */
const createTokenRenewer = ({ getState, dispatch }) => {
  log('createTokenRenewer()');
  let expiredThreshold;

  const processTokenPromiseMap = new Map();

  const processToken = async ({ token }) => {
    log('processToken()', { token, dispatch, getState });
    const state = getState();
    const [
      { default: getExternalData },
      { default: getTimestampOffset, Accuracy },
      { default: getCurrentUnixTimestamp },
    ] = await Promise.all([
      import('../selector/getExternalData.js'),
      import('../selector/getTimestampOffset.js'),
      import('../resource/getCurrentUnixTimestamp.js'),
    ]);
    const loadGetRemoteConfigDataPromise = import(
      '../selector/getRemoteConfigData.js'
    );
    const loadDecodePromise = import('jwt-decode');

    const serverTimestamp = getExternalData(state, ['timestamp'], 'server');
    let timestampOffsetSeconds = null;
    if (serverTimestamp) {
      timestampOffsetSeconds = getTimestampOffset(getState(), Accuracy.SECOND);
    } else {
      log('processToken() fetch time');
      timestampOffsetSeconds =
        await window.__ntp__.asyncGetNtpTimestampOffset();
    }

    const now = getCurrentUnixTimestamp({
      offsetSeconds: timestampOffsetSeconds,
    });
    log('processToken()', { now });

    // get the current exp from the access token
    const decode = (await loadDecodePromise).jwtDecode;
    const decoded = decode(token);
    const currentExp = decoded.exp;
    if (!currentExp) {
      // the token won't expire
      return token;
    }

    const remainDuration = (currentExp - now) * 1000;
    log('processToken()', { currentExp, remainDuration });

    if (!expiredThreshold) {
      const getRemoteConfigData = (await loadGetRemoteConfigDataPromise)
        .default;

      expiredThreshold = getRemoteConfigData(state, TOKEN_EXPIRED_THRESHOLD);
    }
    log('processToken()', { expiredThreshold });

    if (remainDuration < expiredThreshold) {
      const { default: fetchAccessToken, viaTypes } = await import(
        '../action/fetchAccessToken.js'
      );
      if (remainDuration > 0) {
        // Token is about to expire, refresh in parallel
        log('processToken() refresh in parallel');
        dispatch(
          fetchAccessToken({
            triggerToken: token,
            via: viaTypes.CUSTOM_FETCH_PARALLEL,
          })
        );
      } else {
        // Token has expired, need to block fetch and refresh
        log('processToken() block fetch for refresh');
        const loadGetMeDataPromise = import('../selector/getMeData.js');
        const [{ default: getMeData }] = await Promise.all([
          loadGetMeDataPromise,
          dispatch(
            fetchAccessToken({
              triggerToken: token,
              via: 'customFetch-block',
            })
          ),
        ]);
        const newToken = getMeData(getState(), 'token');
        log('processToken()', { newToken });
        return newToken;
      }
    }

    return token;
  };

  return async ({ url, options }) => {
    log('tokenRenewer()', { url, options, dispatch, getState });
    const result = { url, options };

    try {
      const token = options.headers
        .get('Authorization')
        .match(/^Bearer (\S+)$/)?.[1];
      log('tokenRenewer()', { token });

      if (token) {
        let newToken = token;
        if (!processTokenPromiseMap.has(token)) {
          log('tokenRenewer() new process token promise');
          const processTokenPromise = processToken({ token });
          processTokenPromiseMap.set(token, processTokenPromise);
          try {
            newToken = await processTokenPromise;
          } finally {
            // Always clear, even on rejection — otherwise subsequent requests
            // with the same token await a settled-rejected promise and every
            // future call fails until the token actually expires off.
            processTokenPromiseMap.delete(token);
          }
        } else {
          log('tokenRenewer() reuse process token promise');
          newToken = await processTokenPromiseMap.get(token);
        }
        log('tokenRenewer()', { token, newToken });

        if (token !== newToken) {
          options.headers.set('Authorization', `Bearer ${newToken}`);
          log('tokenRenewer() option replaced');
        }
      }
    } catch (error) {
      errorLog('tokenRenewer()', { error });
    }

    return result;
  };
};

export { createTokenRenewer };
